import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { CheckboxField } from '@/components/form/fields/checkbox/Checkbox';
import { TextAndIconField } from '@/components/form/fields/text/TextAndIconInput';
import { CategoryIcons } from '@/components/icons/CategoryIcons';
import { zodResolver } from '@hookform/resolvers/zod';
import { FC } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { Form } from '../../../components/form/Form';
import { Modal } from '../../../components/modal/Modal';
import { ModalProps } from '../../../components/modal/useModal';
import { Card } from '../../../components/sections/Card';
import { useCategories, useUpdateCategory } from '../../../services/api/hooks/catalog';
import TenantRestrictionTree from '@/components/org/TenantRestrictionTree';
import { useProcessOrgTreeForTenantRestrictions } from '@/components/org/useProcessOrgTreeForTenantRestrictions';

const EditCategoryFormFields = z.object({
  name: z.object({
    text: z.string(),
    iconId: z.string()
  }),
  isMainDeviceCategory: z.boolean()
});

type EditCategoryFormFields = z.infer<typeof EditCategoryFormFields>;

export const EditCategoryModal: FC<ModalProps<{ categoryId: string }>> = props => {
  const EditCategory = () => {
    const [categories] = useCategories();
    const categoryId = props.props?.categoryId;
    if (!categoryId) throw new Error('Missing categoryId');
    const category = categories?.find(category => category.id === categoryId);
    const methods = useForm({
      resolver: zodResolver(EditCategoryFormFields),
      defaultValues: {
        name: { text: category?.name, iconId: category?.description },
        isMainDeviceCategory: category?.isMainDevice
      }
    });

    const orgTree = useProcessOrgTreeForTenantRestrictions(category?.hiddenFromTenantIds);
    const updateCategory = useUpdateCategory();

    const onSubmit = async (data: EditCategoryFormFields) => {
      const hiddenFromTenantIds = orgTree.reduce((ids: string[], msp) => {
        if (msp.checked) {
          ids.push(msp.mspId);
        }
        msp.customers.forEach(customer => {
          if (customer.checked) {
            ids.push(customer.customerId);
          }
        });
        return ids;
      }, []);

      await updateCategory.mutateAsync({
        id: categoryId,
        name: data.name.text,
        description: data.name.iconId,
        isMainDevice: data.isMainDeviceCategory,
        hiddenFromTenantIds
      });
      toast.success(`${data.name.text} updated`);
      props.close();
    };

    return (
      <Card className="w-full p-8">
        <Modal.Controls {...props}>
          <h1 className="text-2xl font-semibold">Edit group</h1>
        </Modal.Controls>
        <Form className="flex w-full flex-col gap-3 py-3" methods={methods} onSubmit={onSubmit}>
          <TextAndIconField id="name" label="Name" icons={CategoryIcons} />
          <CheckboxField id="isMainDeviceCategory" label="Main Device Category" />
          
          <div className="space-y-1">
            <h2 className="text-lg font-medium">Hide from Tenants</h2>
            <p className="text-sm text-gray-500">Select MSPs or Customers that should NOT see this category.</p>
            <TenantRestrictionTree data={orgTree} />
          </div>

          <Button className="w-fit place-self-end" variant={ButtonVariant.Primary} type="submit">
            Update
          </Button>
        </Form>
      </Card>
    );
  };

  return (
    <Modal className="md:max-w-xl" {...props}>
      <EditCategory />
    </Modal>
  );
};
